import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dealsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, X, Eye, Trash2, MoreVertical, Filter } from 'lucide-react';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { value: 'in_review', label: 'In Review', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'callback', label: 'Callback', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { value: 'funded', label: 'Funded', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
];

const getStatusBadge = (stage) => {
  const s = STATUSES.find(st => st.value === stage) || { label: stage, color: 'bg-gray-100 text-gray-600 border-gray-300' };
  return <Badge variant="outline" className={`text-[11px] ${s.color}`} data-testid={`status-${stage}`}>{s.label}</Badge>;
};

export default function AdminDeals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState([]);

  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const r = await dealsAPI.list(params);
      setDeals(r.data || []);
    } catch { toast.error('Failed to load deals'); }
    setLoading(false);
  }, [statusFilter, searchQuery]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    try { await dealsAPI.delete(id); toast.success('Deal deleted'); load(); } catch { toast.error('Failed'); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} deal(s)?`)) return;
    try { await dealsAPI.bulkDelete(selected); toast.success('Deleted'); setSelected([]); load(); } catch { toast.error('Failed'); }
  };

  const handleStatusChange = async (dealId, newStatus) => {
    try {
      await dealsAPI.update(dealId, { stage: newStatus });
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: newStatus } : d));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
  };

  return (
    <div data-testid="admin-deals">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 uppercase" data-testid="deals-title">
            {isAdmin ? 'Applications' : 'Track Applications'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{deals.length} deal{deals.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by client, company, salesperson..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="deals-search"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-200 hover:bg-transparent">
                {isAdmin && (
                  <TableHead className="w-10">
                    <BulkSelectCheckbox
                      checked={selected.length === deals.length && deals.length > 0}
                      onCheckedChange={() => setSelected(p => p.length === deals.length ? [] : deals.map(d => d.id))}
                      testId="select-all-deals"
                    />
                  </TableHead>
                )}
                <TableHead className="text-gray-500 text-xs">Client Name</TableHead>
                <TableHead className="text-gray-500 text-xs hidden sm:table-cell">Company</TableHead>
                <TableHead className="text-gray-500 text-xs hidden md:table-cell">Salesperson</TableHead>
                <TableHead className="text-gray-500 text-xs">Status</TableHead>
                <TableHead className="text-gray-500 text-xs hidden lg:table-cell">Date</TableHead>
                <TableHead className="text-gray-500 text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
              ) : deals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <p className="text-gray-500 font-medium">No deals found</p>
                    <p className="text-gray-400 text-sm mt-1">{isAdmin ? 'Deals submitted by staff will appear here' : 'Submit a credit application from a client portfolio'}</p>
                  </TableCell>
                </TableRow>
              ) : deals.map(d => (
                <TableRow key={d.id} className="border-gray-200 text-gray-700 cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/deals/${d.id}`)} data-testid={`deal-row-${d.id}`}>
                  {isAdmin && (
                    <TableCell onClick={e => e.stopPropagation()}>
                      <BulkSelectCheckbox checked={selected.includes(d.id)} onCheckedChange={() => toggleSelect(d.id)} testId={`select-deal-${d.id}`} />
                    </TableCell>
                  )}
                  <TableCell>
                    <p className="font-medium text-sm">{d.client_name || 'Unknown'}</p>
                    <p className="text-xs text-gray-400 sm:hidden">{d.company_name || '--'}</p>
                  </TableCell>
                  <TableCell className="text-xs hidden sm:table-cell">{d.company_name || '--'}</TableCell>
                  <TableCell className="text-xs hidden md:table-cell">{d.created_by || '--'}</TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    {isAdmin ? (
                      <Select value={d.stage} onValueChange={v => handleStatusChange(d.id, v)}>
                        <SelectTrigger className="h-7 w-[120px] text-xs border-0 p-0 shadow-none" data-testid={`change-status-${d.id}`}>
                          {getStatusBadge(d.stage)}
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.filter(s => s.value !== 'all').map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : getStatusBadge(d.stage)}
                  </TableCell>
                  <TableCell className="text-xs text-gray-400 hidden lg:table-cell">
                    {d.created_at ? new Date(d.created_at).toLocaleDateString() : '--'}
                  </TableCell>
                  <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                    <div className="hidden sm:flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500" onClick={() => navigate(`/admin/deals/${d.id}`)} data-testid={`view-deal-${d.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(d.id)} data-testid={`delete-deal-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="sm:hidden">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreVertical className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem onClick={() => navigate(`/admin/deals/${d.id}`)}>
                            <Eye className="w-3.5 h-3.5 mr-2" /> View Details
                          </DropdownMenuItem>
                          {isAdmin && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(d.id)} className="text-red-500">
                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {isAdmin && (
        <BulkActionBar count={selected.length} onDelete={handleBulkDelete} onClear={() => setSelected([])} label="deal(s)" />
      )}
    </div>
  );
}
